// afiliasi.service — kapan bonus rekomendasi cair, dan kapan ditarik.
//
// Dipanggil dari alur pembayaran: cair saat servis PERTAMA orang yang direkomendasikan
// lunas & diverifikasi Finance; ditarik saat servis itu di-refund/dibatalkan.
// Semuanya best-effort — kegagalan memberi bonus TIDAK boleh menggagalkan pembayaran.
import { currentUser } from '@core/session';
import { getSetting } from '@core/settings';
import { pushNotif } from '@core/notif';
import { logAudit } from '@core/audit';
import { cekBonusAfiliasi, ALASAN_TOLAK, DEFAULT_AFILIASI, type AfiliasiCfg } from '@domain/loyalty';
import { todayISO } from '@domain/format';
import * as repo from './afiliasi.repo';

export const afiliasiCfg = (): AfiliasiCfg => ({ ...DEFAULT_AFILIASI, ...getSetting('afiliasi', {}) });

export interface HasilBonus { diberi: boolean; poin: number; kepada?: string; alasan?: string }

/**
 * Coba cairkan bonus untuk perekomendasi pelanggan `customerId`, dipicu tiket `ticketNo`
 * yang baru saja lunas & diverifikasi.
 *
 * Aman dipanggil berulang: indeks unik per tiket menolak bonus kedua, dan pemeriksaannya
 * mengembalikan `sudah-diberi` alih-alih membayar lagi.
 */
export async function cairkanBonus(customerId: number, ticketNo: string): Promise<HasilBonus> {
  const cfg = afiliasiCfg();
  if (!cfg.aktif) return { diberi: false, poin: 0, alasan: ALASAN_TOLAK['mati'] };

  const baru = await repo.pelangganRef(customerId);
  if (!baru?.referrer_id) return { diberi: false, poin: 0 };          // tak direkomendasikan siapa pun
  const perek = await repo.pelangganRef(baru.referrer_id);
  if (!perek) return { diberi: false, poin: 0 };

  const ym = todayISO().slice(0, 7);
  const [sudah, bulanIni] = await Promise.all([
    repo.grantByTiket(ticketNo),
    repo.bonusBulanIni(perek.id, ym),
  ]);

  const cek = cekBonusAfiliasi({
    cfg, perekomendasiId: perek.id, pelangganBaruId: baru.id,
    waPerekomendasi: perek.wa, waPelangganBaru: baru.wa,
    lunasTerverifikasi: true,          // pemanggil hanya memanggil ini setelah verifikasi
    bonusBulanIni: bulanIni, sudahDiberi: !!sudah,
  });
  if (!cek.boleh) return { diberi: false, poin: 0, alasan: cek.alasan ? ALASAN_TOLAK[cek.alasan] : undefined };

  const by = currentUser()?.nama ?? '';
  await repo.beriBonus({
    customer_id: perek.id, poin: cek.poin,
    alasan: `Bonus rekomendasi: ${baru.nama} (${ticketNo})`,
    ticket_no: ticketNo, referred_id: baru.id, berlaku_bulan: cfg.berlakuBulan, by_user: by,
  });
  // Jejak: poin = kewajiban toko, jadi kelahirannya harus bisa ditelusuri seperti uang.
  void logAudit({ action: 'AFILIASI_BONUS', module: 'loyalty', refId: ticketNo, reason: `${cek.poin} poin → ${perek.nama} (rekomendasi ${baru.nama})` });
  try { await pushNotif('CS', null, `🎉 ${perek.nama} dapat ${cek.poin} poin — rekomendasinya (${baru.nama}) sudah servis pertama.`, ticketNo); } catch { /* notif opsional */ }
  return { diberi: true, poin: cek.poin, kepada: perek.nama };
}

/**
 * Tarik bonus yang lahir dari tiket ini (servis di-refund / dibatalkan).
 * Poin servis ikut hilang sendiri karena dihitung dari omzet — bonus afiliasi tidak, maka
 * ia ditarik di sini, lengkap dengan alasannya.
 */
export async function tarikBonusTiket(ticketNo: string, alasan: string): Promise<number> {
  const g = await repo.grantByTiket(ticketNo);
  if (!g) return 0;
  await repo.tarikBonus(g.id, alasan, currentUser()?.nama ?? '');
  void logAudit({ action: 'AFILIASI_TARIK', module: 'loyalty', refId: ticketNo, reason: `${g.poin} poin ditarik — ${alasan}` });
  try { await pushNotif('CS', null, `↩️ ${g.poin} poin bonus rekomendasi ditarik (${alasan}).`, ticketNo); } catch { /* notif opsional */ }
  return g.poin;
}
